import { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';
import { postPortfolioData, getPortfolioData } from '../../../api/apiServices';

export default function PortfolioEditor() {
  const [portfolioData, setPortfolioData] = useState({
    sectionTitle: '',
    sectionSubtitle: '',
    portfolioProjects: [],
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // 🧠 Fetch portfolio data on mount
  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        setLoading(true);
        const data = await getPortfolioData();
        if (data) {
          setPortfolioData(data);
        } else {
          setMessage('ℹ️ No existing portfolio found. You can create one.');
        }
      } catch (error) {
        console.error('Error fetching portfolio data:', error);
        setMessage('❌ Failed to load portfolio data.');
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolio();
  }, []);

  // Handlers
  const handleSectionChange = (field, value) => {
    setPortfolioData((prev) => ({ ...prev, [field]: value }));
  };

  const handleProjectChange = (index, field, value) => {
    setPortfolioData((prev) => ({
      ...prev,
      portfolioProjects: prev.portfolioProjects.map((project, i) =>
        i === index ? { ...project, [field]: value } : project
      ),
    }));
  };

  const handleTechnologyChange = (projectIndex, techIndex, value) => {
    setPortfolioData((prev) => ({
      ...prev,
      portfolioProjects: prev.portfolioProjects.map((project, i) =>
        i === projectIndex
          ? {
              ...project,
              technologies: project.technologies.map((tech, j) =>
                j === techIndex ? value : tech
              ),
            }
          : project
      ),
    }));
  };

  const addProject = () => {
    setPortfolioData((prev) => ({
      ...prev,
      portfolioProjects: [
        ...prev.portfolioProjects,
        {
          projectTitle: 'New Project',
          category: 'Website',
          description: 'Project description',
          image: '',
          technologies: ['React'],
          liveUrl: '',
          githubUrl: '',
          featured: false,
        },
      ],
    }));
  };

  const removeProject = (index) => {
    setPortfolioData((prev) => ({
      ...prev,
      portfolioProjects: prev.portfolioProjects.filter((_, i) => i !== index),
    }));
  };

  const addTechnology = (projectIndex) => {
    setPortfolioData((prev) => ({
      ...prev,
      portfolioProjects: prev.portfolioProjects.map((project, i) =>
        i === projectIndex
          ? { ...project, technologies: [...project.technologies, 'New Tech'] }
          : project
      ),
    }));
  };

  const removeTechnology = (projectIndex, techIndex) => {
    setPortfolioData((prev) => ({
      ...prev,
      portfolioProjects: prev.portfolioProjects.map((project, i) =>
        i === projectIndex
          ? {
              ...project,
              technologies: project.technologies.filter((_, j) => j !== techIndex),
            }
          : project
      ),
    }));
  };

  // ✅ Save data to backend
  const handleSave = async () => {
    try {
      setLoading(true);
      setMessage('');
      await postPortfolioData(portfolioData);
      setMessage('✅ Portfolio saved successfully!');
    } catch (error) {
      console.error('Error saving portfolio:', error);
      setMessage('❌ Failed to save portfolio.');
    } finally {
      setLoading(false);
    }
  };

  // --- UI ---
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-800 flex items-center">
          <i className="ri-folder-line mr-3 text-emerald-600"></i>
          Portfolio Section Settings
        </h2>
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg p-4 flex items-center justify-between z-50">
  <p className="text-slate-600 font-medium">
    {message || "All changes are saved locally until you click Save."}
  </p>
  
        <Button
          onClick={handleSave}
          disabled={loading}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
      </div>

      {/* Section Info */}
      <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
        <h3 className="text-xl font-bold text-slate-800 mb-4">Section Header</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-slate-700 font-semibold mb-3">Section Title</label>
            <input
              type="text"
              value={portfolioData.sectionTitle}
              onChange={(e) => handleSectionChange('sectionTitle', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
            />
          </div>
          <div>
            <label className="block text-slate-700 font-semibold mb-3">Section Subtitle</label>
            <input
              type="text"
              value={portfolioData.sectionSubtitle}
              onChange={(e) => handleSectionChange('sectionSubtitle', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
            />
          </div>
        </div>
      </div>

      {/* Projects */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-slate-800">Portfolio Projects</h3>
          
          <Button
            onClick={addProject}
            size="sm"
            variant="outline"
            className="border-emerald-200 text-emerald-600 hover:bg-emerald-50"
          >
            <i className="ri-add-line mr-2"></i>Add Project
          </Button>
        </div>

        <div className="space-y-6">
          {portfolioData.portfolioProjects.map((project, index) => (
            <div key={index} className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <h4 className="font-semibold text-slate-800">Project {index + 1}</h4>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={project.featured || false}
                      onChange={(e) =>
                        handleProjectChange(index, 'featured', e.target.checked)
                      }
                      className="w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
                    />
                    <span className="text-sm text-slate-600 font-medium">Featured</span>
                  </label>
                </div>
                {portfolioData.portfolioProjects.length > 0 && (
                  <button
                    onClick={() => removeProject(index)}
                    className="text-red-500 hover:text-red-700 transition-colors duration-300"
                  >
                    <i className="ri-delete-bin-line text-lg"></i>
                  </button>
                )}
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <input
                    type="text"
                    value={project.projectTitle || ''}
                    onChange={(e) =>
                      handleProjectChange(index, 'projectTitle', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Project title"
                  />
                  <input
                    type="text"
                    value={project.category || ''}
                    onChange={(e) =>
                      handleProjectChange(index, 'category', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    placeholder="Category"
                  />
                  <input
                    type="url"
                    value={project.liveUrl || ''}
                    onChange={(e) =>
                      handleProjectChange(index, 'liveUrl', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    placeholder="Live URL"
                  />
                  <textarea
                    value={project.description || ''}
                    onChange={(e) =>
                      handleProjectChange(index, 'description', e.target.value)
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg resize-none"
                    placeholder="Project description"
                  />
                </div>

                <div className="space-y-4">
                  <textarea
                    value={project.image || ''}
                    onChange={(e) =>
                      handleProjectChange(index, 'image', e.target.value)
                    }
                    rows={4}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg resize-none"
                    placeholder="Project image or URL"
                  />
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-slate-600 font-medium">Technologies</label>
                      <button
                        onClick={() => addTechnology(index)}
                        className="text-emerald-600 hover:text-emerald-700"
                      >
                        <i className="ri-add-line text-lg"></i>
                      </button>
                    </div>
                    {project.technologies?.map((tech, techIndex) => (
                      <div key={techIndex} className="flex items-center gap-2 mb-2">
                        <input
                          type="text"
                          value={tech}
                          onChange={(e) =>
                            handleTechnologyChange(index, techIndex, e.target.value)
                          }
                          className="flex-1 px-3 py-2 border border-slate-300 rounded-lg"
                        />
                        {project.technologies.length > 1 && (
                          <button
                            onClick={() => removeTechnology(index, techIndex)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <i className="ri-close-line text-lg"></i>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
